import React from "react";
import { Link } from "react-router-dom";

/**
 * Parses inline formatting:
 * - Markdown links: [Label](url) or [Label](file:///url)
 * - Bold: **text**
 * - Italic: *text* or _text_
 * - Inline code: `code`
 */
export function parseInline(text, onNavigate) {
  if (!text || typeof text !== "string") return null;

  // Regex to match markdown links, bold, italic, code
  // 1: Link [text](url)
  // 2: Bold **text**
  // 3: Italic *text*
  // 4: Code `text`
  const tokenRegex = /(\[([^\]]+)\]\((?:file:\/\/\/)?([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)/g;

  const elements = [];
  let lastIndex = 0;
  let match;

  while ((match = tokenRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      elements.push(text.substring(lastIndex, match.index));
    }

    if (match[1]) {
      // Link: match[2] is label, match[3] is url
      let url = match[3];
      if (!url.startsWith("/") && !url.startsWith("http")) {
        url = "/" + url;
      }

      // Security Guardrail: Never expose raw uploads paths, localhost ports, or internal storage URLs in text
      if (/:\d+\/uploads\/|\/uploads\/|localhost:\d+/i.test(url)) {
        continue;
      }

      const isFile = /\.(pdf|xlsx|xls|csv|docx|zip|png|jpg)(\?.*)?$/i.test(url) || url.includes('/download/');

      if (isFile) {
        elements.push(
          <a
            key={`file-${match.index}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 my-0.5 rounded-md font-bold text-xs bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60 shadow-2xs hover:bg-emerald-100 hover:text-emerald-800 transition cursor-pointer"
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>{match[2]}</span>
          </a>
        );
      } else {
        elements.push(
          <Link
            key={`link-${match.index}`}
            to={url}
            onClick={() => {
              if (onNavigate) onNavigate();
            }}
            className="inline-flex items-center gap-1 font-semibold text-primary-600 underline underline-offset-2 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
          >
            <span>{match[2]}</span>
            <svg className="h-3 w-3 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </Link>
        );
      }
    } else if (match[4]) {
      // Bold: match[5]
      elements.push(
        <strong key={`bold-${match.index}`} className="font-semibold text-slate-900 dark:text-white">
          {match[5]}
        </strong>
      );
    } else if (match[6]) {
      // Italic: match[7]
      elements.push(
        <em key={`em-${match.index}`} className="italic text-slate-700 dark:text-slate-300">
          {match[7]}
        </em>
      );
    } else if (match[8]) {
      // Code: match[9]
      elements.push(
        <code
          key={`code-${match.index}`}
          className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-primary-700 dark:bg-slate-800 dark:text-primary-300"
        >
          {match[9]}
        </code>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    elements.push(text.substring(lastIndex));
  }

  return elements.length === 1 && typeof elements[0] === "string" ? elements[0] : elements;
}

/**
 * Checks if a table cell value represents a numeric/currency value
 */
function isNumericCell(str) {
  if (!str) return false;
  const cleaned = str.replace(/[₹$€£,%\s(kg)(MT)]/gi, "").trim();
  return cleaned !== "" && !isNaN(Number(cleaned));
}

/**
 * Helper to render parsed table blocks into a clean, mobile-responsive HTML table
 */
function renderTable(tableLines, keyIndex, onNavigate) {
  if (!tableLines || tableLines.length === 0) return null;

  // Split lines into cells
  const parsedRows = tableLines
    .map((line) => {
      // Clean leading and trailing pipes
      const trimmed = line.trim();
      const stripped = trimmed.replace(/^\|/, "").replace(/\|$/, "");
      return stripped.split("|").map((cell) => cell.trim());
    })
    .filter((row) => {
      // Filter out markdown separator row (|---|---|)
      const isSeparator = row.every((c) => /^:?-+:?$/.test(c.trim()));
      return !isSeparator && row.length > 0 && row.some((c) => c !== "");
    });

  if (parsedRows.length === 0) return null;

  const headerRow = parsedRows[0];
  const bodyRows = parsedRows.slice(1);

  return (
    <div
      key={`table-wrapper-${keyIndex}`}
      className="my-3 overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-xs dark:border-slate-800 dark:bg-slate-900"
    >
      <div className="overflow-x-auto max-w-full">
        <table className="w-full min-w-[340px] border-collapse text-left text-[11px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/90 dark:border-slate-800 dark:bg-slate-800/60">
              {headerRow.map((col, idx) => {
                const alignRight = isNumericCell(col) || (bodyRows[0] && isNumericCell(bodyRows[0][idx]));
                return (
                  <th
                    key={idx}
                    className={`px-3 py-2 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap ${
                      alignRight ? "text-right" : "text-left"
                    }`}
                  >
                    {parseInline(col, onNavigate)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
            {bodyRows.map((row, rIdx) => (
              <tr
                key={rIdx}
                className="transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/40 odd:bg-white even:bg-slate-50/30 dark:odd:bg-slate-900 dark:even:bg-slate-900/60"
              >
                {row.map((cell, cIdx) => {
                  const alignRight =
                    isNumericCell(headerRow[cIdx]) || isNumericCell(cell);
                  return (
                    <td
                      key={cIdx}
                      className={`px-3 py-2 text-slate-700 dark:text-slate-300 ${
                        alignRight ? "text-right font-medium text-slate-900 dark:text-slate-100" : "text-left"
                      }`}
                    >
                      {parseInline(cell, onNavigate)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Main ChatMessageContent component
 * Transforms markdown into clean, professional UI blocks:
 * - HTML Tables without raw pipe characters
 * - Numbered badge lists
 * - Clean bullet lists
 * - Highlighted callouts for Notes and Totals
 * - Clickable links and bold metrics
 */
export default function ChatMessageContent({ content, onNavigate }) {
  if (!content) return null;

  // Zero-Leak Guardrail: strip internal backend ports, /uploads/ links, and bare URLs from text presentation
  const sanitizedContent = content
    .replace(/\[(?:Download|View|Open|File|PDF|Report|Invoice)[^\]]*\]\((?:https?:\/\/[^\s\)]+|[^\s\)]*\/uploads\/[^\s\)]*|[^\s\)]*\/api\/[^\s\)]*)\)/gi, "")
    .replace(/https?:\/\/(?:localhost|127\.0\.0\.1|[\w\.-]+):\d+\/[^\s\)\],]*/gi, "")
    .replace(/https?:\/\/[^\s\)\],]+\/uploads\/[^\s\)\],]*/gi, "")
    .replace(/\b(?:uploads\/selling-master[^\s\)\],]*)/gi, "")
    .replace(/[📁\s]*\*{0,2}Download:?\*{0,2}\s*[\r\n]+/gi, "");

  const lines = sanitizedContent.split("\n");
  const blocks = [];
  let currentTable = null;
  let currentList = null; // { type: 'ul' | 'ol', items: [] }

  const flushTable = () => {
    if (currentTable && currentTable.length > 0) {
      blocks.push({
        type: "table",
        lines: currentTable,
      });
      currentTable = null;
    }
  };

  const flushList = () => {
    if (currentList && currentList.items.length > 0) {
      blocks.push(currentList);
      currentList = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const trimmed = rawLine.trim();

    // 1. Table line detection (contains pipe '|' and looks like markdown table)
    if (trimmed.startsWith("|") || (trimmed.includes("|") && trimmed.endsWith("|"))) {
      flushList();
      if (!currentTable) currentTable = [];
      currentTable.push(trimmed);
      continue;
    } else {
      flushTable();
    }

    // 2. Empty line
    if (!trimmed) {
      flushList();
      blocks.push({ type: "spacer" });
      continue;
    }

    // Divider line: --- or *** or ___
    if (/^([-*_]){3,}$/.test(trimmed)) {
      flushList();
      blocks.push({ type: "divider" });
      continue;
    }

    // 3. Headings: ### or ## or #
    const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      flushList();
      blocks.push({
        type: "heading",
        level: headingMatch[1].length,
        text: headingMatch[2],
      });
      continue;
    }

    // 4. Note / Warning Callout
    if (/^\*\*Note:?\*\*/i.test(trimmed) || /^Note:/i.test(trimmed) || trimmed.startsWith(">")) {
      flushList();
      const noteText = trimmed.replace(/^>\s*/, "").replace(/^\*\*Note:?\*\*\s*/i, "").trim();
      blocks.push({
        type: "callout",
        title: "Note",
        text: noteText,
      });
      continue;
    }

    // 5. Total / Metric Highlight line (e.g., **Total Sales Volume:** 2,645 kg)
    if (/^\*\*Total\s+([^:]+):\*\*\s*(.+)$/i.test(trimmed)) {
      flushList();
      const totalMatch = trimmed.match(/^\*\*Total\s+([^:]+):\*\*\s*(.+)$/i);
      blocks.push({
        type: "metric",
        label: `Total ${totalMatch[1]}`,
        value: totalMatch[2],
      });
      continue;
    }

    // 6. Numbered list item: 1. Item
    const olMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
    if (olMatch) {
      if (!currentList || currentList.type !== "ol") {
        flushList();
        currentList = { type: "ol", items: [] };
      }
      currentList.items.push({
        num: olMatch[1],
        content: olMatch[2],
      });
      continue;
    }

    // 7. Bullet list item: - Item or * Item or • Item
    const ulMatch = trimmed.match(/^[-*•]\s+(.+)$/);
    if (ulMatch) {
      if (!currentList || currentList.type !== "ul") {
        flushList();
        currentList = { type: "ul", items: [] };
      }
      currentList.items.push(ulMatch[1]);
      continue;
    }

    // 8. Regular paragraph
    flushList();
    blocks.push({
      type: "paragraph",
      text: trimmed,
    });
  }

  flushTable();
  flushList();

  return (
    <div className="space-y-2 text-xs leading-relaxed">
      {blocks.map((block, idx) => {
        switch (block.type) {
          case "table":
            return renderTable(block.lines, idx, onNavigate);

          case "heading": {
            const hasEmojiPrefix = /^(\p{Extended_Pictographic}|\uD83C[\uDF00-\uDFFF]|\uD83D[\uDC00-\uDE4F]|\uD83E[\uDD00-\uDDFF])/u.test(block.text);
            return (
              <h4
                key={idx}
                className="mt-3 mb-1 font-bold text-slate-900 dark:text-white text-xs flex items-center gap-1.5"
              >
                {!hasEmojiPrefix && (
                  <span className="h-3 w-1 rounded-full bg-primary-500 shrink-0" />
                )}
                <span>{parseInline(block.text, onNavigate)}</span>
              </h4>
            );
          }

          case "divider":
            return (
              <hr
                key={idx}
                className="my-2.5 border-t border-slate-200/90 dark:border-slate-800"
              />
            );

          case "callout":
            return (
              <div
                key={idx}
                className="my-2.5 flex items-start gap-2.5 rounded-xl border border-amber-200/90 bg-amber-50/70 p-2.5 text-[11px] text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/20 dark:text-amber-200 shadow-2xs"
              >
                <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-200 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300 font-bold text-[10px] mt-0.5">
                  i
                </div>
                <div className="flex-1">
                  <span className="font-semibold">{block.title}: </span>
                  {parseInline(block.text, onNavigate)}
                </div>
              </div>
            );

          case "metric":
            return (
              <div
                key={idx}
                className="my-1.5 flex items-center justify-between rounded-lg border border-slate-200/80 bg-slate-50/80 px-3 py-2 text-[11px] dark:border-slate-800 dark:bg-slate-800/50"
              >
                <span className="font-medium text-slate-600 dark:text-slate-400">
                  {block.label}
                </span>
                <span className="font-bold text-slate-900 dark:text-white">
                  {parseInline(block.value, onNavigate)}
                </span>
              </div>
            );

          case "ol":
            return (
              <div key={idx} className="my-2 space-y-2">
                {block.items.map((item, itemIdx) => (
                  <div key={itemIdx} className="flex items-start gap-2.5 group">
                    <span className="flex h-5 min-w-[20px] px-1 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-800 dark:bg-primary-950/80 dark:text-primary-300 font-bold text-[10px] border border-primary-300/80 dark:border-primary-700/80 shadow-2xs">
                      {item.num}
                    </span>
                    <div className="flex-1 text-slate-900 dark:text-slate-100 font-medium">
                      {parseInline(item.content, onNavigate)}
                    </div>
                  </div>
                ))}
              </div>
            );

          case "ul":
            return (
              <div key={idx} className="my-1.5 space-y-1.5 pl-1.5">
                {block.items.map((item, itemIdx) => (
                  <div key={itemIdx} className="flex items-start gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary-500 inline-block mt-1.5 shrink-0" />
                    <div className="flex-1 text-slate-800 dark:text-slate-200">
                      {parseInline(item, onNavigate)}
                    </div>
                  </div>
                ))}
              </div>
            );

          case "paragraph":
            return (
              <p key={idx} className="text-slate-800 dark:text-slate-200">
                {parseInline(block.text, onNavigate)}
              </p>
            );

          case "spacer":
            return <div key={idx} className="h-1" />;

          default:
            return null;
        }
      })}
    </div>
  );
}
